import { inject, Injectable } from '@angular/core';
import {
    EquipmentTypes,
    featureSelectedStyleOptions,
    IDatiqMapGeoJson,
    IDatiqMapGeoJsonFeature,
    IIqMcMapGeoJsonFeaturePropertiesStyles,
    ltColorMaps,
    LtRatio,
    LtRatioPayload,
    McQuery,
} from '../../models';
import {
    defaultDatiqMapStyleConfiguration,
    IDatiqMapMetric,
    IDatiqMapMetricCell,
} from './models';
import { isNil } from 'lodash';
import { IqMcMapUtilsService } from './iq-mc-map-utils.service';

@Injectable({
    providedIn: 'root',
})
export class LtMapService {

    constructor() {}

    private _utils = inject(IqMcMapUtilsService);

    computeFeatures(
        ratioMap: LtRatioPayload,
        featureCollection: IDatiqMapGeoJson,
        query: McQuery,
    ): IDatiqMapGeoJson {
        const computedFeatures: IDatiqMapGeoJsonFeature[] =
            featureCollection.features.map(
                (feature: IDatiqMapGeoJsonFeature): IDatiqMapGeoJsonFeature => {
                    const coloredFeature: IDatiqMapGeoJsonFeature =
                        this._mapDataToFeature(
                            feature,
                            ratioMap,
                            query?.equipmentType as EquipmentTypes,
                        );
                    return coloredFeature;
                },
            );
        return { ...featureCollection, features: computedFeatures };
    }

    setMapFeatures(
        map: google.maps.Map,
        featureCollection: IDatiqMapGeoJson,
    ): void {
        this._utils.setGeoJsonToMap(map, featureCollection);
        this._utils.setMapStyles(map);
        this._utils.addOverlayMap(map);
    }

    private _mapDataToFeature(
        feature: IDatiqMapGeoJsonFeature,
        ratios: LtRatioPayload,
        equipmentType: EquipmentTypes,
    ): IDatiqMapGeoJsonFeature {
        const featureLtData = this._findDataForFeature(feature, ratios);
        const style: IIqMcMapGeoJsonFeaturePropertiesStyles =
            this._mapDataToStyle(featureLtData, feature, equipmentType);
        return {
            ...feature,
            properties: {
                ...feature.properties,
                style,
            },
        };
    }
    private _findDataForFeature(
        feature: IDatiqMapGeoJsonFeature,
        ltDataFeatures: LtRatioPayload,
    ) {
        return ltDataFeatures.find(
            (ltData) => ltData.geoCode === feature.properties.code,
        );
    }

    private _mapDataToStyle(
        featureLtData: LtRatio,
        feature: IDatiqMapGeoJsonFeature,
        equipmentType: EquipmentTypes,
    ): IIqMcMapGeoJsonFeaturePropertiesStyles {
        const fillColor: string = this._getFillColorForData(
            featureLtData,
            equipmentType,
        );
        return {
            ...feature?.properties?.style,
            fillColor,
        };
    }

    private _getFillColorForData(
        featureLtData: LtRatio,
        equipmentType: EquipmentTypes,
    ): string {
        return (
            this._getDataMapMetricCell(featureLtData, equipmentType)?.color ??
            defaultDatiqMapStyleConfiguration.fillColor
        );
    }

    private _getDataMapMetricCell(
        ltData: LtRatio,
        equipmentType: EquipmentTypes,
    ): IDatiqMapMetricCell {
        if (isNil(ltData?.ratio)) {
            return null;
        }
        const colorMap: IDatiqMapMetric = ltColorMaps[equipmentType];
        return this._mapDataToFillColor(ltData.ratio, colorMap);
    }

    private _mapDataToFillColor(
        ratio: any,
        colorMap: IDatiqMapMetric,
    ): IDatiqMapMetricCell {
        return colorMap?.styles?.find(
            (style: IDatiqMapMetricCell) =>
                ratio >= style?.range?.low && ratio <= style?.range.high,
        );
    }
}
